import Vue from 'vue';
import VueRouter from 'vue-router';
import axios from '@axios';
import operacoes from '@/router/operacoes/index';
import { isUserLoggedIn, getUserData, getHomeRouteForLoggedInUser } from '@/auth/utils';
import { canNavigate } from '@/libs/acl/routeProtection';
import ability from '@/libs/acl/ability';
import {
  permissionRefreshContext,
  shouldRefreshPermissions,
  synchronizeCanonicalHome,
} from '@/libs/acl/homePermission';
import catalogoProdutos from '@/router/catalogo-produtos';
import relatorios from '@/router/relatorios';
import store from '@/store';
import auth from './routers/auth.router';
import cadastroGerais from './cadastros-gerais';
import cadastrosRoutes from './cadastros/cadastros.router';
import dkpCLientes from './dkp-clientes';
import sistemaRouter from './sistema';
import ptcRoutes from './ptcRoutes';
import dashboardRoutes from './dashboards/dashboard.router';
import crmRoutes from './crm/crm.router';
import campaignRoutes from './crm/campaign.router';
import marketingRoutes from './crm/marketing.router';
import gamificationRoutes from './gamification/gamification.router';
import integracaoErpRoutes from './configuracao/integracaoErp.router';
import aiChatConfigRoutes from './configuracao/aiChatConfig.router';
import jobErpRoutes from './configuracao/jobErp.router';
import budgetRoutes from './budget/budget.router';
import budgetLiderRoutes from './budgetLider/budgetLider.router';
import representanteHierarquiaRoutes from './representante/representanteHierarquia.router';
import assistenciaTecnicaRoutes from './assistencia-tecnica';

Vue.use(VueRouter);

/**
 * Prefixa '/:tenantSlug' em todas as rotas da aplicação, de forma que o slug do
 * tenant fique sempre na URL (ex.: /macmillan/home, /macmillan/login).
 * Reescreve apenas paths absolutos (começam com '/') — paths relativos de rotas
 * filhas são compostos automaticamente pelo Vue Router. Recursivo p/ children.
 */
const TENANT_PREFIX = '/:tenantSlug';

// Primeiros segmentos de URL que são rotas REAIS da aplicação — nunca devem ser
// interpretados como slug de tenant (senão /home vira o "tenant home", poluindo
// o localStorage e o contexto). O preview do Replit abre em /home, por isso é
// essencial tratar esses caminhos como sem-tenant.
const RESERVED_FIRST_SEGMENTS = [
  'home',
  'login',
  'second-page',
  'error-404',
  'not-authorized',
  'documentacao',
];

function isReservedSlug(slug) {
  return !!slug && RESERVED_FIRST_SEGMENTS.includes(slug);
}

// Slug de tenant "limpo" do localStorage (ignora valores reservados poluídos).
function getStoredTenantSlug() {
  const raw = localStorage.getItem('tenantSlug');
  return raw && !isReservedSlug(raw) ? raw : null;
}

// Redirect para caminhos reservados acessados SEM slug (ex.: /home, /second-page).
// Se houver um tenant conhecido, mantém o caminho sob /<slug>/...; senão, pede a empresa.
function bareReservedRedirect(to) {
  const slug = getStoredTenantSlug();
  if (slug) {
    return { path: `/${slug}${to.path}`, query: to.query, hash: to.hash };
  }
  return { name: 'tenant-select' };
}

function withTenantPrefix(routes) {
  return routes.map((route) => {
    const novo = { ...route };
    if (typeof novo.path === 'string') {
      if (novo.path === '') {
        novo.path = TENANT_PREFIX;
      } else if (novo.path.startsWith('/') && !novo.path.startsWith(TENANT_PREFIX)) {
        novo.path = TENANT_PREFIX + novo.path;
      }
    }
    if (Array.isArray(novo.children)) {
      novo.children = withTenantPrefix(novo.children);
    }
    return novo;
  });
}

const appRoutes = withTenantPrefix([
  ...cadastrosRoutes,
  ...auth,
  ...ptcRoutes,
  ...sistemaRouter,
  ...cadastroGerais,
  ...dkpCLientes,
  ...catalogoProdutos,
  ...operacoes,
  ...relatorios,
  ...dashboardRoutes,
  ...crmRoutes,
  ...campaignRoutes,
  ...marketingRoutes,
  ...gamificationRoutes,
  ...integracaoErpRoutes,
  ...aiChatConfigRoutes,
  ...jobErpRoutes,
  ...budgetRoutes,
  ...budgetLiderRoutes,
  ...representanteHierarquiaRoutes,
  ...assistenciaTecnicaRoutes,
  {
    path: '/documentacao',
    name: 'documentation',
    component: () => import('@/views/documentation/Index.vue'),
    meta: {
      pageTitle: 'Documentação',
      resource: 'Auth',
    },
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/home/Index.vue'),
    meta: {
      action: 'read',
      resource: 'Home',
      explicitModule: true,
    },
  },
  {
    path: '/second-page',
    name: 'second-page',
    component: () => import('@/views/SecondPage.vue'),
    meta: {
      pageTitle: 'Second Page',
    },
  },
  {
    path: '/error-404',
    name: 'error-404',
    component: () => import('@/views/error/Error404.vue'),
    meta: {
      layout: 'full',
      resource: 'Auth',
    },
  },
]);

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    // Raiz (sem slug): página "informe sua empresa"
    {
      path: '/',
      name: 'tenant-select',
      component: () => import('@/views/Login/SelecionarEmpresa.vue'),
      meta: {
        layout: 'full',
        resource: 'Auth',
      },
    },
    // URL antiga sem slug → manda para a seleção de empresa
    { path: '/login', redirect: { name: 'tenant-select' } },
    ...appRoutes,
    // Caminhos reservados acessados SEM slug (ex.: /home — o preview do Replit abre
    // aqui). Devem ir para o tenant conhecido ou para a seleção de empresa — NUNCA
    // ser tratados como o "tenant home".
    { path: '/home', redirect: bareReservedRedirect },
    { path: '/second-page', redirect: bareReservedRedirect },
    { path: '/error-404', redirect: bareReservedRedirect },
    { path: '/not-authorized', redirect: bareReservedRedirect },
    { path: '/documentacao', redirect: bareReservedRedirect },
    // Slug isolado (ex.: /macmillan) → home daquele tenant (preserva o slug)
    {
      path: TENANT_PREFIX,
      redirect: (to) => ({ name: 'home', params: { tenantSlug: to.params.tenantSlug } }),
    },
    // URL desconhecida DENTRO de um tenant (ex.: /macmillan/qualquer-coisa) →
    // 404 preservando o slug da URL (não depende do contexto/localStorage)
    {
      path: `${TENANT_PREFIX}/*`,
      redirect: (to) => ({ name: 'error-404', params: { tenantSlug: to.params.tenantSlug } }),
    },
    // URL desconhecida SEM slug → pede a empresa (não força um 404 sem tenant)
    {
      path: '*',
      redirect: { name: 'tenant-select' },
    },
  ],
});

// Rotas que não exigem slug do tenant na URL
const SLUGLESS_ROUTES = ['tenant-select'];

let refreshedPermissionContext = null;

async function refreshActiveHomePermission(force = false) {
  let perfilLogado;
  try {
    perfilLogado = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
  } catch (_) {
    perfilLogado = {};
  }
  const perfilId = perfilLogado.id_perfil_acesso;
  const token = localStorage.getItem('accessToken');
  const context = permissionRefreshContext(token, perfilId);
  if (!context) return false;
  if (!shouldRefreshPermissions(refreshedPermissionContext, context, force)) return true;

  try {
    const { data } = await axios.get('/auth/me');
    const activeProfile = data && data.activeProfile;
    // Uma troca de perfil/token enquanto a requisição estava em voo invalida
    // a resposta antiga, evitando aplicar permissões do perfil anterior.
    let latestProfile;
    try {
      latestProfile = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
    } catch (_) {
      latestProfile = {};
    }
    const latestContext = permissionRefreshContext(
      localStorage.getItem('accessToken'),
      latestProfile.id_perfil_acesso,
    );
    if (latestContext !== context) return false;

    const synchronized = synchronizeCanonicalHome(ability.rules, activeProfile, perfilId);
    if (!synchronized) return false;
    localStorage.setItem('userPermissions', JSON.stringify(synchronized));
    ability.update(synchronized);
    refreshedPermissionContext = context;
    return true;
  } catch (_) {
    return false;
  }
}

router.beforeEach(async (to, from, next) => {
  // 1. Página de seleção de empresa não precisa de slug
  if (SLUGLESS_ROUTES.includes(to.name)) {
    return next();
  }

  // 2. Garante que o slug do tenant esteja presente na rota de destino.
  //    Navegações por nome (this.$router.push({ name })) não passam o param —
  //    aqui ele é herdado do contexto atual ou do localStorage, sem precisar
  //    editar as centenas de chamadas existentes.
  //    Slugs reservados (home, login, ...) NÃO são tenants — tratados como ausentes.
  const rawSlug = to.params.tenantSlug;
  const slug = isReservedSlug(rawSlug) ? null : rawSlug;
  if (!slug) {
    const fromSlug = from && from.params && from.params.tenantSlug;
    const fallback = (fromSlug && !isReservedSlug(fromSlug) ? fromSlug : null) || getStoredTenantSlug();
    if (fallback && to.name) {
      return next({
        name: to.name,
        params: { ...to.params, tenantSlug: fallback },
        query: to.query,
        hash: to.hash,
        replace: true,
      });
    }
    // Sem slug em lugar nenhum → pede a empresa
    return next({ name: 'tenant-select' });
  }

  // 3. Sincroniza o slug da URL para o localStorage (consumido pelo axios em
  //    x-tenant-slug). A lógica de master/impersonação continua no axios.
  localStorage.setItem('tenantSlug', slug);

  const isLoggedIn = isUserLoggedIn();
  const routesPublic = ['login', 'loginWithSegment', 'forgot-password', 'auth-reset-password-v2', 'misc-not-authorized', 'error-404'];

  if (isLoggedIn) {
    store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', false);
    store.commit('appConfig/UPDATE_CONTENT_WIDTH', 'full');
  } else {
    store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', true);
    store.commit('appConfig/UPDATE_CONTENT_WIDTH', 'boxed');
  }

  if (routesPublic.includes(to.name)) {
    return next();
  }
  if (!isLoggedIn && to.matched.some(route => route.meta.activeAdminDkpOnly)) {
    return next({ name: 'login', params: { tenantSlug: slug }, query: { redirect: to.fullPath } });
  }

  // Atualiza ao menos uma vez por sessão/perfil para eliminar permissões Home
  // antigas do localStorage. Ao abrir Home, consulta sempre a matriz atual:
  // assim URL direta e menu não dependem de logout/login após uma remoção.
  if (isLoggedIn) {
    const homeRoute = to.matched.some((route) => route.meta.explicitModule && ['Home', 'AssistenciaTecnica'].includes(route.meta.resource));
    const refreshed = await refreshActiveHomePermission(homeRoute);
    if (homeRoute && !refreshed) {
      return next({ name: 'misc-not-authorized', params: { tenantSlug: slug }, replace: true });
    }
  }

  // Static help is available to every signed-in profile, independently of
  // operational module permissions. It never grants access to those modules.
  // Do not make the documentation a public route.
  if (to.name === 'documentation') {
    return next(isLoggedIn
      ? undefined
      : { name: 'login', params: { tenantSlug: slug }, query: { redirect: to.fullPath } });
  }

  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    const userData = getUserData();
    return next(getHomeRouteForLoggedInUser(userData ? userData.role : null));
  }

  // A matriz de módulos é a fonte de verdade também para navegação direta.
  // Sem isso, um redirect/fallback poderia abrir uma rota que o perfil acabou
  // de perder no Gerenciar Módulos.
  if (isLoggedIn && !canNavigate(to)) {
    return next({ name: 'misc-not-authorized', params: { tenantSlug: slug }, replace: true });
  }

  // Rota exclusiva para AdminDKP
  if (to.meta.adminDkpOnly) {
    const impersonateSlug = localStorage.getItem('dkpImpersonateSlug');
    const isImpersonating = !!impersonateSlug && impersonateSlug !== 'dkp';

    const perfilLogado = (() => {
      try { return JSON.parse(localStorage.getItem('perfilLogado') || '{}'); } catch { return {}; }
    })();
    const userData = (() => {
      try { return JSON.parse(localStorage.getItem('userData') || '{}'); } catch { return {}; }
    })();

    // usuario_master=1 vem direto do backend no login — é a fonte mais confiável
    const isUserMaster = Number(userData.usuario_master) === 1;

    const isAdminDkp = isImpersonating
      || isUserMaster
      || perfilLogado.perfil_acesso_nome === 'AdminDKP'
      || perfilLogado.perfil_acesso_nome === 'Admin';

    if (!isAdminDkp) {
      return next({ name: 'home' });
    }
  }

  return next();
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
});

export default router;
